import React, { useState } from 'react';
import { PickupGame, SportType, AthleteProfile } from '../types';
import { getGameDateInfo } from '../utils/dateUtils';
import { 
  Flame, 
  MapPin, 
  Calendar as CalendarIcon, 
  Users, 
  Plus, 
  Share2, 
  Check, 
  Search, 
  Filter, 
  Clock,
  Sparkles,
  Mail,
  MessageSquare,
  Copy,
  Send,
  X,
  LayoutGrid,
  Trophy,
  Crown
} from 'lucide-react';

import { getActiveSports, getSportLabel } from '../utils/sportUtils';
import { GameCalendarView } from './GameCalendarView';
import { triggerHaptic } from '../utils/haptics';
import { RegionalCoverageMap } from './regional/RegionalCoverageMap';
import { CourtPOI } from '../types';
import { PlayerOfTheGameModal } from './PlayerOfTheGameModal';
import { PickupGamesEmptyIllustration } from './illustrations/PickupGamesEmptyIllustration';

interface PickupGamesViewProps {
  games: PickupGame[];
  user: AthleteProfile;
  courts?: CourtPOI[];
  onOpenHostGame: () => void;
  onJoinGame: (gameId: string) => void;
  onShareGame: (game: PickupGame) => void;
}

export const PickupGamesView: React.FC<PickupGamesViewProps> = ({
  games,
  user,
  courts = [],
  onOpenHostGame,
  onJoinGame,
  onShareGame,
}) => {
  const [sportFilter, setSportFilter] = useState<SportType | 'all'>(user.primarySport || 'all');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');

  const activeSports = getActiveSports(user);

  // Quick invite modal state for a specific game
  const [activeInviteGame, setActiveInviteGame] = useState<PickupGame | null>(null);
  const [quickEmails, setQuickEmails] = useState('');
  const [quickPhones, setQuickPhones] = useState('');
  const [inviteSentToast, setInviteSentToast] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [quickInviteToast, setQuickInviteToast] = useState<string | null>(null);
  const [copiedGameId, setCopiedGameId] = useState<string | null>(null);

  // Player of the Game MVP modal state
  const [votingGame, setVotingGame] = useState<PickupGame | null>(null);

  const handleVoteMVP = (gameId: string, playerId: string) => {
    const targetGame = games.find((g) => g.id === gameId) || votingGame;
    if (!targetGame) return;

    if (!targetGame.mvpVotes) targetGame.mvpVotes = {};
    targetGame.mvpVotes[playerId] = (targetGame.mvpVotes[playerId] || 0) + 1;

    if (!targetGame.mvpVotedUserIds) targetGame.mvpVotedUserIds = [];
    if (!targetGame.mvpVotedUserIds.includes(user.id)) {
      targetGame.mvpVotedUserIds.push(user.id);
    }

    let maxV = 0;
    let winnerId = '';
    let winnerName = '';
    Object.entries(targetGame.mvpVotes).forEach(([pId, count]) => {
      if (count > maxV) {
        maxV = count;
        winnerId = pId;
        const p = targetGame.playersList.find((pl) => pl.id === pId);
        if (p) winnerName = p.name;
      }
    });

    targetGame.mvpWinnerId = winnerId;
    targetGame.mvpWinnerName = winnerName;

    setVotingGame({ ...targetGame });
  };

  const filteredGames = games.filter((game) => {
    const matchesSport = sportFilter === 'all' || game.sport === sportFilter;
    const matchesLevel = levelFilter === 'all' || game.level === levelFilter;
    const matchesSearch =
      game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      game.courtName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      game.hostName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSport && matchesLevel && matchesSearch;
  });

  const handleQuickInvite = async (game: PickupGame) => {
    triggerHaptic('light');
    const gameJoinUrl = `${window.location.origin}?gameId=${game.id}`;
    const shareText = `🔥 Join my ${game.sport.toUpperCase()} pickup run: "${game.title}" at ${game.courtName} (${game.dateTime})! Sign up here: ${gameJoinUrl}`;

    // 1. Copy link to clipboard automatically
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(gameJoinUrl);
      }
    } catch (err) {
      console.error('Clipboard copy error:', err);
    }

    setCopiedGameId(game.id);

    // 2. Open native share menu if available
    let openedNativeShare = false;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pickup Run: ${game.title}`,
          text: shareText,
          url: gameJoinUrl,
        });
        openedNativeShare = true;
      } catch (err) {
        // User closed/cancelled native share modal
      }
    }

    // 3. Call prop handler
    onShareGame(game);

    // 4. Toast notification feedback
    setQuickInviteToast(
      openedNativeShare
        ? `Link copied & native share menu opened for "${game.title}"! 📲`
        : `Game join link copied to clipboard! Ready to send to friends. 📋`
    );

    setTimeout(() => {
      setCopiedGameId(null);
    }, 2500);

    setTimeout(() => {
      setQuickInviteToast(null);
    }, 4000);
  };

  const handleSendQuickInvites = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeInviteGame) return;

    const emailList = quickEmails.split(',').map((e) => e.trim()).filter(Boolean);
    const phoneList = quickPhones.split(',').map((p) => p.trim()).filter(Boolean);

    if (emailList.length === 0 && phoneList.length === 0) return;

    // Append to game
    activeInviteGame.invitedEmails = [...(activeInviteGame.invitedEmails || []), ...emailList];
    activeInviteGame.invitedPhones = [...(activeInviteGame.invitedPhones || []), ...phoneList];

    setInviteSentToast(`Invites sent to ${emailList.length} emails and ${phoneList.length} phone numbers with registration link!`);
    setTimeout(() => {
      setInviteSentToast(null);
      setActiveInviteGame(null);
      setQuickEmails('');
      setQuickPhones('');
    }, 1800);
  };

  const handleCopyLink = (url?: string) => {
    const linkToCopy = url || `https://playgrounds.app/join?game=game_pickup&code=RUN2026`;
    navigator.clipboard?.writeText(linkToCopy);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1800);
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-8 animate-fadeIn text-white">
      {/* Toast Notification Banner for Quick Invite */}
      {quickInviteToast && (
        <div className="p-4 bg-lime-400 text-black font-extrabold text-xs rounded-2xl shadow-2xl flex items-center justify-between animate-fadeIn border-2 border-black/20">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-black shrink-0 animate-bounce" />
            <span>{quickInviteToast}</span>
          </div>
          <button
            type="button"
            onClick={() => setQuickInviteToast(null)}
            className="p-1 hover:bg-black/10 rounded-lg transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Top Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div>
          <div className="flex items-center space-x-2">
            <Flame className="w-6 h-6 text-rose-400 fill-rose-400/20" />
            <h1 className="text-2xl font-black italic uppercase tracking-tight text-white">
              Local Pickup Games & Tournaments
            </h1>
          </div>
          <p className="text-xs text-indigo-200/70 mt-1 font-semibold">
            Host games, join nearby runs, and send email/SMS invites to players & unregistered recruits.
          </p>
        </div>

        <button
          onClick={() => {
            triggerHaptic('medium');
            onOpenHostGame();
          }}
          className="px-6 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic rounded-2xl shadow-lg shadow-lime-400/20 flex items-center justify-center space-x-2 transition active:scale-95 uppercase text-sm tracking-wider"
        >
          <Plus className="w-5 h-5 stroke-[3]" />
          <span>Host Pickup Game</span>
        </button>
      </div>

      {/* Regional Coverage & Court Density Map */}
      <RegionalCoverageMap
        registeredCity={user.registeredCity || user.location?.city}
        registeredState={user.registeredState || user.location?.state}
        courts={courts}
      />

      {/* Filter & Search Controls */}
      <div className="flex flex-col md:flex-row gap-3 bg-indigo-900/40 p-4 rounded-2xl border border-white/10">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-3.5 text-indigo-300/60" />
          <input
            type="text"
            placeholder="Search game title, court, or host..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-indigo-950 text-white text-sm font-medium rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSportFilter(user.primarySport)}
            className={`px-3 py-2 rounded-xl text-xs font-black italic uppercase transition ${
              sportFilter === user.primarySport
                ? 'bg-lime-400 text-black shadow-md'
                : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
            }`}
          >
            My Sport: {getSportLabel(user.primarySport)}
          </button>

          <button
            type="button"
            onClick={() => setSportFilter('all')}
            className={`px-3 py-2 rounded-xl text-xs font-black italic uppercase transition ${
              sportFilter === 'all'
                ? 'bg-lime-400 text-black shadow-md'
                : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
            }`}
          >
            All Sports
          </button>

          {/* Sport filter select */}
          <select
            value={sportFilter}
            onChange={(e) => setSportFilter(e.target.value as any)}
            className="bg-indigo-950 text-white text-xs font-bold px-3 py-2 rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
          >
            <option value="all">All Sports Options...</option>
            <option value="basketball">Basketball 🏀</option>
            <option value="baseball">Baseball ⚾</option>
            <option value="softball">Softball 🥎</option>
            <option value="pickleball">Pickleball 🏓</option>
            <option value="soccer">Soccer ⚽</option>
            <option value="volleyball">Volleyball 🏐</option>
            <option value="football">Football 🏈</option>
            <option value="tennis">Tennis 🎾</option>
          </select>

          {/* Level filter pill */}
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="bg-indigo-950 text-white text-xs font-bold px-3 py-2 rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
          >
            <option value="all">All Levels</option>
            <option value="Casual">Casual</option>
            <option value="Competitive">Competitive</option>
            <option value="Varsity / Intramural">Varsity / Intramural</option>
          </select>

          {/* View Mode Toggle: Calendar vs List */}
          <div className="flex items-center bg-indigo-950 p-1 rounded-xl border border-white/10 text-xs font-black uppercase ml-auto">
            <button
              type="button"
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 rounded-lg transition flex items-center space-x-1.5 ${
                viewMode === 'calendar'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              <CalendarIcon className="w-3.5 h-3.5" />
              <span>Calendar</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-lg transition flex items-center space-x-1.5 ${
                viewMode === 'list'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Grid List</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main View Area: Calendar vs Cards Grid */}
      {viewMode === 'calendar' ? (
        <GameCalendarView
          games={filteredGames}
          user={user}
          onOpenHostGame={onOpenHostGame}
          onJoinGame={onJoinGame}
          onShareGame={onShareGame}
        />
      ) : filteredGames.length === 0 ? (
        <div className="my-6">
          <PickupGamesEmptyIllustration
            title="No Scheduled Pickup Runs Found"
            selectedSport={sportFilter}
            onResetFilters={() => {
              setSportFilter('all');
              setLevelFilter('all');
              setSearchQuery('');
            }}
            onOpenHostGame={onOpenHostGame}
          />
        </div>
      ) : (
        /* Games Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGames.map((game) => {
            const isJoined = game.playersList.some((p) => p.id === user.id);
            const hasInvites = (game.invitedEmails?.length || 0) > 0 || (game.invitedPhones?.length || 0) > 0;
            const dateInfo = getGameDateInfo(game.dateTime);

            return (
              <div
                key={game.id}
                className={`p-6 rounded-[2rem] border shadow-xl transition flex flex-col justify-between space-y-4 relative overflow-hidden ${
                  dateInfo.isPast
                    ? 'bg-indigo-950/50 border-white/10 opacity-80 hover:opacity-100 hover:border-amber-400/40'
                    : 'bg-indigo-950/80 border-white/10 hover:border-lime-400/50'
                }`}
              >
                {game.isHostedByMe && (
                  <span className="absolute top-0 right-0 bg-rose-500 text-white font-black italic text-[10px] uppercase px-3 py-1 rounded-bl-xl shadow-sm">
                    Hosted by You
                  </span>
                )}

                <div className="space-y-3">
                  <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black italic uppercase bg-lime-400 text-black">
                      {game.sport}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black italic uppercase bg-indigo-900 text-indigo-200 border border-white/10">
                      {game.level}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black italic uppercase ${dateInfo.badgeStyle}`}>
                      {dateInfo.badgeLabel}
                    </span>
                  </div>

                  <h3 className="font-extrabold text-lg text-white leading-snug">
                    {game.title}
                  </h3>

                  <p className="text-xs text-indigo-200/80 line-clamp-2">
                    {game.description}
                  </p>

                  <div className="space-y-1.5 text-xs text-indigo-200/70 pt-1">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1.5 text-rose-400 shrink-0" />
                      <span className="font-bold text-white">{game.courtName}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className={`w-4 h-4 mr-1.5 shrink-0 ${dateInfo.isPast ? 'text-amber-400' : 'text-lime-400'}`} />
                      <span className={dateInfo.isPast ? 'text-amber-300 font-bold' : 'text-white'}>
                        {dateInfo.formattedLabel}
                      </span>
                    </div>
                  </div>

                {/* Player of the Game MVP Banner */}
                {(() => {
                  const votes = game.mvpVotes || {};
                  let topPlayerId = game.mvpWinnerId;
                  let topVotes = 0;
                  Object.entries(votes).forEach(([pId, count]) => {
                    if (count > topVotes) {
                      topVotes = count;
                      topPlayerId = pId;
                    }
                  });
                  const topPlayer = game.playersList.find((p) => p.id === topPlayerId);
                  if (!topPlayer || topVotes === 0) return null;

                  return (
                    <div className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-black p-2 rounded-xl text-xs font-black italic uppercase flex items-center justify-between shadow-md">
                      <span className="flex items-center space-x-1.5">
                        <Crown className="w-4 h-4 fill-black shrink-0 animate-bounce" />
                        <span>MVP Leader: {topPlayer.name} ({topVotes} {topVotes === 1 ? 'vote' : 'votes'})</span>
                      </span>
                      <Trophy className="w-4 h-4 fill-black" />
                    </div>
                  );
                })()}

                {/* Invite Badge Status */}
                {hasInvites && (
                  <div className="bg-lime-400/10 border border-lime-400/30 p-2 rounded-xl text-[10px] font-semibold text-lime-300 flex items-center justify-between">
                    <span className="flex items-center space-x-1">
                      <Send className="w-3 h-3 text-lime-400" />
                      <span>
                        Invites Sent: {game.invitedEmails?.length || 0} Emails • {game.invitedPhones?.length || 0} SMS
                      </span>
                    </span>
                    <span className="font-mono text-[9px] text-indigo-300">Auto Sign-Up Active</span>
                  </div>
                )}

                {/* Player Slots & Avatars */}
                <div className="pt-2 border-t border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-indigo-200 flex items-center">
                      <Users className="w-3.5 h-3.5 mr-1 text-lime-400" />
                      Roster ({game.currentPlayers}/{game.maxPlayers})
                    </span>
                    <span className="text-[11px] font-black italic text-lime-400">
                      {game.maxPlayers - game.currentPlayers} spots open
                    </span>
                  </div>

                  <div className="flex items-center space-x-1 overflow-x-auto py-1">
                    {game.playersList.map((player) => (
                      <img
                        key={player.id}
                        src={player.avatar}
                        alt={player.name}
                        title={`${player.name} (${player.rsvp})`}
                        className="w-7 h-7 rounded-full object-cover ring-2 ring-indigo-950 shrink-0"
                        referrerPolicy="no-referrer"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Actions */}
              <div className="pt-3 border-t border-white/10 flex items-center justify-between gap-2">
                {/* Quick Invite Button */}
                <button
                  type="button"
                  onClick={() => handleQuickInvite(game)}
                  className={`px-3 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 shrink-0 shadow-md ${
                    copiedGameId === game.id
                      ? 'bg-lime-400 text-black shadow-lime-400/30 ring-2 ring-lime-300'
                      : 'bg-indigo-900 hover:bg-lime-400 hover:text-black text-indigo-100 border border-white/10'
                  }`}
                  title="Quick Invite: Copy join link and open native share menu"
                >
                  <Share2 className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>{copiedGameId === game.id ? 'Copied & Shared! ✓' : 'Quick Invite'}</span>
                </button>

                {/* Vote Player of the Game MVP Button */}
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('medium');
                    setVotingGame(game);
                  }}
                  className="px-3 py-2 rounded-xl bg-amber-400 hover:bg-yellow-300 text-black font-black italic text-xs uppercase transition flex items-center space-x-1 shrink-0 shadow-md shadow-amber-400/20"
                  title="Vote for Player of the Game MVP"
                >
                  <Trophy className="w-3.5 h-3.5 fill-black" />
                  <span>MVP</span>
                </button>

                {/* Direct Email/SMS Modal Invite Button */}
                <button
                  type="button"
                  onClick={() => setActiveInviteGame(game)}
                  className="p-2.5 rounded-xl bg-indigo-900 hover:bg-indigo-800 text-indigo-200 border border-white/10 transition"
                  title="Send Direct Email or SMS Text invites with registration link"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>

                {/* Join / RSVP Button */}
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('success');
                    onJoinGame(game.id);
                  }}
                  className={`flex-1 py-2 rounded-xl font-black italic text-xs uppercase transition flex items-center justify-center space-x-1.5 ${
                    isJoined
                      ? 'bg-lime-400/20 text-lime-300 border border-lime-400/40'
                      : 'bg-rose-500 hover:bg-rose-600 text-white shadow-md shadow-rose-500/20'
                  }`}
                >
                  {isJoined ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>RSVP'd Going</span>
                    </>
                  ) : (
                    <span>Join Pickup Game</span>
                  )}
                </button>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Quick Email & Text Message Invite Modal */}
      {activeInviteGame && (
        <div className="fixed inset-0 z-50 bg-indigo-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-900 border border-white/15 rounded-[2.5rem] p-6 max-w-md w-full text-white space-y-4 relative shadow-2xl">
            <button
              onClick={() => setActiveInviteGame(null)}
              className="absolute top-5 right-5 p-2 rounded-full bg-indigo-950 text-indigo-200 hover:text-white border border-white/10"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="space-y-1">
              <span className="text-[10px] font-black italic uppercase text-lime-400 tracking-wider">
                Send Direct Player Invites
              </span>
              <h3 className="text-lg font-black italic uppercase text-white">
                {activeInviteGame.title}
              </h3>
              <p className="text-xs text-indigo-200/70">
                Send email and SMS text messages. Unregistered players will receive a direct link to sign up and auto-join this game.
              </p>
            </div>

            {inviteSentToast ? (
              <div className="p-4 bg-lime-400/20 border border-lime-400/40 rounded-2xl text-center space-y-2">
                <Check className="w-8 h-8 text-lime-400 mx-auto animate-bounce" />
                <p className="text-xs font-bold text-lime-300">{inviteSentToast}</p>
              </div>
            ) : (
              <form onSubmit={handleSendQuickInvites} className="space-y-3 text-xs font-semibold">
                {/* Quick Add Friends */}
                <div className="space-y-1">
                  <span className="text-[10px] text-indigo-300 font-bold block">⚡ Quick Add Teammates:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { name: 'Jordan Bell', email: 'jordan@school.edu' },
                      { name: 'Marcus Vance', email: 'marcus.vance@varsity.org' },
                      { name: 'Maya Lin', email: 'maya.lin@selectball.com' },
                    ].map((p) => {
                      const isAdded = quickEmails.includes(p.email);
                      return (
                        <button
                          key={p.email}
                          type="button"
                          onClick={() => {
                            if (isAdded) {
                              setQuickEmails(quickEmails.replace(p.email, '').replace(/,\s*,/, ',').trim());
                            } else {
                              setQuickEmails(quickEmails ? `${quickEmails}, ${p.email}` : p.email);
                            }
                          }}
                          className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border transition ${
                            isAdded
                              ? 'bg-lime-400 text-black border-lime-400'
                              : 'bg-indigo-950 text-indigo-200 border-white/10 hover:text-white'
                          }`}
                        >
                          + {p.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-indigo-200 flex items-center mb-1 font-bold">
                    <Mail className="w-3.5 h-3.5 text-lime-400 mr-1" />
                    Email Addresses (comma-separated for multiple)
                  </label>
                  <input
                    type="text"
                    placeholder="teammate1@gmail.com, recruit2@school.edu, player3@ball.org"
                    value={quickEmails}
                    onChange={(e) => setQuickEmails(e.target.value)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>

                <div>
                  <label className="text-indigo-200 flex items-center mb-1 font-bold">
                    <MessageSquare className="w-3.5 h-3.5 text-rose-400 mr-1" />
                    SMS Text Numbers (comma-separated for multiple)
                  </label>
                  <input
                    type="text"
                    placeholder="+1 (555) 019-2831, +1 (555) 482-9102, +1 (555) 333-2211"
                    value={quickPhones}
                    onChange={(e) => setQuickPhones(e.target.value)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>

                <div className="bg-indigo-950 p-3 rounded-xl border border-white/10 space-y-1.5">
                  <span className="text-[10px] text-indigo-300 font-bold block">Game Registration Link:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-mono text-lime-300 truncate flex-1">
                      {activeInviteGame.inviteUrl || `https://playgrounds.app/join?game=${activeInviteGame.id}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopyLink(activeInviteGame.inviteUrl)}
                      className="px-2.5 py-1 bg-indigo-800 hover:bg-indigo-700 text-white text-[10px] rounded-lg font-bold flex items-center space-x-1"
                    >
                      {copiedLink ? <Check className="w-3 h-3 text-lime-400" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedLink ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-1.5"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Automated Email & Text Invites</span>
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Player of the Game MVP Voting Modal */}
      <PlayerOfTheGameModal
        game={votingGame}
        user={user}
        isOpen={!!votingGame}
        onClose={() => setVotingGame(null)}
        onVoteMVP={handleVoteMVP}
      />
    </div>
  );
};

